/* eslint-disable no-return-assign */
/* eslint-disable import/no-cycle */
import { checkHighestQualifiedProductionType } from ".";
import { appContextStore } from "../../../common/redux/appContext";
import { membershipContentStore } from "../../../common/redux/membershipContent";
import { IInitialProduction } from "../../redux/ProductionReducer/ProductionReducer.types";
import { MembershipLevelsEnum } from "../enums/membershipLevelsEnum";

interface IProductionValidationObject {
  commission: { isValid: boolean; isHighestQualifyingMethod: boolean };
  premium: { isValid: boolean; isHighestQualifyingMethod: boolean };
  income: {
    isValidRisk: boolean;
    isValidNewBusiness: boolean;
    isHighestQualifyingMethod: boolean;
  };
}

export const validateRiskProtection = (
  commission: IInitialProduction[],
  premium: IInitialProduction[],
  income: IInitialProduction[],
  commissionTotal: number,
  premiumTotal: number,
  incomeTotal: number,
  commissionRiskProtection: number,
  premiumRiskProtection: number,
  incomeRiskProtection: number,
  incomeNewBusiness: number
) => {
  const { applicationDefinition } = appContextStore.getState();
  const commissionMDRTThreshold =
    applicationDefinition.productionRequirements.mdrt.commission.total;
  const premiumMDRTThreshold =
    applicationDefinition.productionRequirements.mdrt.premium.total;
  const incomeMDRTThreshold =
    applicationDefinition.productionRequirements.mdrt.income.total;
  const incomeRiskProtectionThreshold =
    applicationDefinition.productionRequirements.mdrt.income.unlimited;
  const incomeNewBusinessThreshold =
    applicationDefinition.productionRequirements.mdrt.income.newBusiness;

  const {
    commissionQualifiedLevel,
    premiumQualifiedLevel,
    incomeQualifiedLevel,
  } = checkHighestQualifiedProductionType(
    commissionTotal,
    premiumTotal,
    incomeTotal
  );

  let commissionValid = commission.length === 0;
  let premiumValid = premium.length === 0;
  let incomeValid = income.length === 0;

  if (commission.length > 0 && commissionTotal >= commissionMDRTThreshold)
    commissionValid = commissionRiskProtection >= commissionMDRTThreshold * 0.5;
  if (premium.length > 0 && premiumTotal >= premiumMDRTThreshold)
    premiumValid = premiumRiskProtection >= premiumMDRTThreshold * 0.5;
  if (income.length > 0 && incomeTotal >= incomeMDRTThreshold) {
    incomeValid =
      incomeRiskProtection >= incomeRiskProtectionThreshold &&
      incomeNewBusiness >= incomeNewBusinessThreshold;
  }

  const validProductionTypes: number[] = [];
  if (commission.length > 0 && commissionValid)
    validProductionTypes.push(commissionQualifiedLevel);
  if (premium.length > 0 && premiumValid)
    validProductionTypes.push(premiumQualifiedLevel);
  if (income.length > 0 && incomeValid)
    validProductionTypes.push(incomeQualifiedLevel);

  let highestValidLevelId = 0;
  let highestLevelId = 0;
  let highestValidLevel = "";
  let highestLevel = "";

  validProductionTypes.forEach(
    (id) =>
      (highestValidLevelId =
        id > highestValidLevelId ? id : highestValidLevelId)
  );
  [
    commissionQualifiedLevel,
    premiumQualifiedLevel,
    incomeQualifiedLevel,
  ].forEach(
    (id) => (highestLevelId = id > highestLevelId ? id : highestLevelId)
  );

  const productionTypes: IProductionValidationObject = {
    commission: {
      isValid:
        commission.length > 0
          ? commissionRiskProtection >= commissionMDRTThreshold * 0.5
          : true,
      isHighestQualifyingMethod:
        commission.length > 0 &&
        commissionValid &&
        commissionQualifiedLevel === highestValidLevelId,
    },
    premium: {
      isValid:
        premium.length > 0
          ? premiumRiskProtection >= premiumMDRTThreshold * 0.5
          : true,
      isHighestQualifyingMethod:
        premium.length > 0 &&
        premiumValid &&
        premiumQualifiedLevel === highestValidLevelId,
    },
    income: {
      isValidRisk:
        income.length > 0
          ? incomeRiskProtection >= incomeRiskProtectionThreshold
          : true,
      isValidNewBusiness:
        income.length > 0
          ? incomeNewBusiness >= incomeNewBusinessThreshold
          : true,
      isHighestQualifyingMethod:
        income.length > 0 &&
        incomeValid &&
        incomeQualifiedLevel === highestValidLevelId,
    },
  };

  switch (highestValidLevelId) {
    case 1:
      highestValidLevel = MembershipLevelsEnum.MDRT;
      break;
    case 2:
      highestValidLevel = MembershipLevelsEnum.COT;
      break;
    case 3:
      highestValidLevel = MembershipLevelsEnum.TOT;
      break;
    default:
      break;
  }
  switch (highestLevelId) {
    case 1:
      highestLevel = MembershipLevelsEnum.MDRT;
      break;
    case 2:
      highestLevel = MembershipLevelsEnum.COT;
      break;
    case 3:
      highestLevel = MembershipLevelsEnum.TOT;
      break;
    default:
      break;
  }

  return {
    highestValidLevel,
    highestLevel,
    productionTypes,
  };
};

export const getProductionErrorMessages = (
  productionTypes: IProductionValidationObject
) => {
  const { productionStep } = membershipContentStore.getState();
  const { commission, premium, income } = productionTypes;
  const errorMessages: string[] = [];

  if (
    !commission.isValid &&
    !commission.isHighestQualifyingMethod &&
    !premium.isHighestQualifyingMethod &&
    !income.isHighestQualifyingMethod
  ) {
    errorMessages.push(productionStep.commissionRiskErrorMessage);
  }
  if (
    !premium.isValid &&
    !premium.isHighestQualifyingMethod &&
    !commission.isHighestQualifyingMethod &&
    !income.isHighestQualifyingMethod
  ) {
    errorMessages.push(productionStep.premiumRiskErrorMessage);
  }
  if (
    !income.isValidRisk &&
    !income.isHighestQualifyingMethod &&
    !commission.isHighestQualifyingMethod &&
    !premium.isHighestQualifyingMethod
  ) {
    errorMessages.push(productionStep.incomeRiskErrorMessage);
  }
  if (
    !income.isValidNewBusiness &&
    !income.isHighestQualifyingMethod &&
    !commission.isHighestQualifyingMethod &&
    !premium.isHighestQualifyingMethod
  ) {
    errorMessages.push(productionStep.incomeOtherNewBusinessErrorMessage);
  }

  return errorMessages;
};
